import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { Map as Maplibre, Marker } from 'maplibre-gl';
import useResizeObserver from '@react-hook/resize-observer';
import './ResizableMap.module.css';
import MapMarker from './MapMarker';

export interface Coordinates {
  lng: number;
  lat: number;
}

export interface ResizableMapProps {
  coordinates: Coordinates;
  maptilerKey: string;
  s?: CSSProperties;
}

const ResizableMap: FC<ResizableMapProps> = ({
  coordinates,
  maptilerKey,
  s = {
    height: '20rem',
    width: '20rem',
  },
}) => {
  const [maplibre, setMaplibre] = useState<Maplibre | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapElement = useRef<HTMLDivElement | null>(null);
  const mapMarker = useRef<HTMLElement | null>(null);
  const { lng, lat } = coordinates;

  // Resize the map whenever the mapElement itself has a different size.
  // This is useful because, in addition to the window resizing, many other
  // events in the page can cause mapElement to resize and we need the map
  // to respond accordingly
  useResizeObserver(mapContainer, (): void => {
    if (maplibre) maplibre.resize();
  });

  useEffect(
    () => {
      if (!mapElement.current) return;

      setMaplibre(
        new Maplibre({
          container: mapElement.current,
          style: `https://api.maptiler.com/maps/streets/style.json?key=${maptilerKey}`,
          center: [lng, lat],
          zoom: 15,
          trackResize: false, // trackResize only resizes on window resize. This case is covered by useResizeObserver
        }),
      );
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maptilerKey],
  );

  useEffect(() => {
    if (!(maplibre && mapMarker.current)) return;

    new Marker(mapMarker.current).setLngLat([lng, lat]).addTo(maplibre);
  }, [maplibre, lng, lat]);

  return (
    <div style={s} ref={mapContainer}>
      <div ref={mapElement} style={{ height: '100%' }}>
        <MapMarker ref={mapMarker} />
      </div>
    </div>
  );
};

export default ResizableMap;
